/**
 * FileUploadStage Component
 *
 * Stage 2 of the import wizard - handles file upload via drag-and-drop or click.
 * Displays selected file information and supports file removal.
 *
 * @module components/import/stages
 */

import {
  DocumentArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/chariot-ui';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { Checkbox } from '@/components/form/Checkbox';
import { ImportAction } from '@/components/import/types';
import { formatFileSize } from '@/components/import/utils';
import { cn } from '@/utils/classname';

/**
 * Props for FileUploadStage component
 */
export interface FileUploadStageProps {
  /** Selected import action from stage 1 */
  selectedAction: ImportAction;
  /** Currently uploaded file (if any) */
  uploadedFile: File | null;
  /** Callback when file is uploaded */
  onFileUpload: (file: File | null) => void;
  /** Callback to return to previous stage */
  onBack: () => void;
  /** Selected configuration values (for config options like auto_approve) */
  selectedConfigs?: Record<string, boolean | string>;
  /** Callback when a configuration option changes */
  onConfigChange?: (key: string, value: boolean | string) => void;
}

/**
 * FileUploadStage Component
 *
 * Provides drag-and-drop file upload interface with visual feedback.
 * Supports CSV, JSON, XML, and XLSX file types with file size validation.
 *
 * @param props - Component props
 * @returns File upload stage JSX
 */
export function FileUploadStage({
  selectedAction,
  uploadedFile,
  onFileUpload,
  onBack: _onBack,
  selectedConfigs,
  onConfigChange,
}: FileUploadStageProps) {
  const [fileError, setFileError] = useState<string | null>(null);

  // Use action-specific accepted file types, or default to common formats
  const acceptedFileTypes = selectedAction.acceptedFileTypes || {
    'text/csv': ['.csv'],
    'application/json': ['.json'],
    'text/xml': ['.xml'],
    'application/xml': ['.xml'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
      '.xlsx',
    ],
    'application/vnd.ms-excel': ['.xls'],
  };

  const formatsLabel =
    selectedAction.acceptedFormatsLabel || 'CSV, JSON, XML, XLSX';

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: acceptedFiles => {
      if (acceptedFiles.length > 0) {
        setFileError(null); // Clear any previous errors
        onFileUpload(acceptedFiles[0]);
      }
    },
    onDropRejected: fileRejections => {
      if (fileRejections.length > 0) {
        const rejection = fileRejections[0];
        const errors = rejection.errors.map(e => e.message).join(', ');
        setFileError(`File rejected: ${errors}`);
      }
    },
    maxFiles: 1,
    maxSize: 1024 * 1024 * 1024, // 1GB (files upload directly to S3, not held in memory)
    accept: acceptedFileTypes,
  });

  return (
    <>
      <h3 className="text-chariot-text-primary text-lg font-bold">
        Upload File
      </h3>
      <p className="text-chariot-text-secondary text-sm">
        Upload {selectedAction.label} scan results
      </p>

      {/* Drag-and-drop zone */}
      <div
        {...getRootProps()}
        className={cn(
          'cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors',
          isDragActive
            ? 'border-chariot-brand bg-chariot-brand/5'
            : 'border-chariot-stroke hover:border-chariot-stroke-hover'
        )}
      >
        <input {...getInputProps()} />
        <DocumentArrowUpIcon className="text-chariot-text-tertiary mx-auto mb-4 size-12" />
        {isDragActive ? (
          <p className="text-chariot-text-primary font-medium">
            Drop file here...
          </p>
        ) : (
          <>
            <p className="text-chariot-text-primary font-medium">
              Drag and drop file here
            </p>
            <p className="text-chariot-text-secondary mt-1 text-sm">
              or click to browse
            </p>
            <p className="text-chariot-text-tertiary mt-2 text-xs">
              Supported formats: {formatsLabel} (max 1GB)
            </p>
          </>
        )}
      </div>

      {/* Error message display */}
      {fileError && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
          <p className="text-sm text-red-600 dark:text-red-400">{fileError}</p>
        </div>
      )}

      {/* Selected file display */}
      {uploadedFile && (
        <div className="bg-chariot-background-secondary rounded-lg p-4">
          <div className="flex items-center gap-3">
            <DocumentIcon className="text-chariot-brand size-8 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-chariot-text-primary truncate font-medium">
                {uploadedFile.name}
              </p>
              <p className="text-chariot-text-secondary text-sm">
                {formatFileSize(uploadedFile.size)}
              </p>
            </div>
            <Button
              label="Remove"
              size="xs"
              styleType="secondary"
              onClick={() => {
                setFileError(null); // Clear error when removing file
                onFileUpload(null);
              }}
              startIcon={<XMarkIcon className="size-4" />}
            />
          </div>
        </div>
      )}

      {/* Configuration Options */}
      {selectedAction.configs &&
        selectedAction.configs.length > 0 &&
        onConfigChange && (
          <div className="mt-4 space-y-3">
            <h3 className="text-chariot-text-primary text-sm font-medium">
              Import Options
            </h3>
            {selectedAction.configs.map(config => {
              if (config.type === 'checkbox') {
                return (
                  <Checkbox
                    key={config.key}
                    label={config.label}
                    value={selectedConfigs?.[config.key] ? 'true' : 'false'}
                    onChange={e => {
                      const newValue = Boolean(e.target.value);
                      onConfigChange(config.key, newValue);
                    }}
                    name={config.key}
                    info={{ text: config.description }}
                  />
                );
              }

              // TODO: Add text and select type rendering in future
              return null;
            })}
          </div>
        )}
    </>
  );
}
